import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CarCard from "@/components/CarCard";
import AvailabilitySearch from "@/components/AvailabilitySearch";
import { Seo } from "@/components/Seo";
import { LANDING_PAGES } from "@/data/landingPages";
import { BELO_PROVIDER } from "@/data/cityLocations";
import { ChevronDown, Phone } from "lucide-react";

const SITE = "https://belorentals.com";

// Curated internal links shown on every landing page for SEO cross-linking.
const RELATED_LINKS = [
  ["/logan-airport-car-rental", "Logan Airport Car Rental"],
  ["/boston-airport-car-rental", "Boston Airport Car Rental"],
  ["/cheap-car-rental-logan-airport", "Cheap Car Rental at Logan"],
  ["/boston-weekly-car-rental", "Weekly Car Rental Boston"],
  ["/economy-car-rental-boston", "Economy Car Rental Boston"],
  ["/long-term-car-rental-boston", "Long-Term Car Rental Boston"],
  ["/contactless-car-rental-boston", "Contactless Car Rental Boston"],
  ["/cape-cod-car-rental", "Cape Cod Car Rental"],
  ["/boston-to-salem-car-rental", "Boston → Salem"],
  ["/boston-to-plymouth-car-rental", "Boston → Plymouth"],
  ["/logan-airport-to-gillette-stadium-car-rental", "Logan → Gillette Stadium"],
  ["/boston-to-marthas-vineyard-car-rental", "Boston → Martha's Vineyard"],
  ["/boston-to-nantucket-car-rental", "Boston → Nantucket"],
  ["/boston-to-berkshires-car-rental", "Boston → The Berkshires"],
];

const HERO_IMAGES = {
  "boston-airport-car-rental": "/marketing/logan-airport.webp",
  "cheap-car-rental-logan-airport": "/marketing/logan-airport.webp",
  "weekly-car-rental-logan-airport": "/marketing/logan-airport.webp",
  "contactless-car-rental-boston": "/marketing/logan-airport.webp",
  "boston-to-salem-car-rental": "/marketing/salem.webp",
  "logan-airport-to-salem-car-rental": "/marketing/salem.webp",
  "boston-to-plymouth-car-rental": "/marketing/plymouth.webp",
  "boston-to-marthas-vineyard-car-rental": "/marketing/marthas-vineyard.webp",
  "boston-to-nantucket-car-rental": "/marketing/nantucket.webp",
  "cape-cod-car-rental": "https://images.unsplash.com/photo-1509215082347-65c4e2c9161e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxDYXBlJTIwQ29kJTIwbGlnaHRob3VzZSUyMHN1bnNldHxlbnwwfHx8fDE3ODI1MjAyNzB8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "boston-to-cape-cod-car-rental": "https://images.unsplash.com/photo-1509215082347-65c4e2c9161e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxDYXBlJTIwQ29kJTIwbGlnaHRob3VzZSUyMHN1bnNldHxlbnwwfHx8fDE3ODI1MjAyNzB8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "logan-airport-to-cape-cod-car-rental": "https://images.unsplash.com/photo-1509215082347-65c4e2c9161e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxDYXBlJTIwQ29kJTIwbGlnaHRob3VzZSUyMHN1bnNldHxlbnwwfHx8fDE3ODI1MjAyNzB8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "boston-to-foxborough-car-rental": "https://images.unsplash.com/photo-1714213450890-3f465d40ed46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdGFkaXVtJTIwYXJjaGl0ZWN0dXJlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgyOTE1MDQ3fDA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "logan-airport-to-gillette-stadium-car-rental": "https://images.unsplash.com/photo-1714213450890-3f465d40ed46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdGFkaXVtJTIwYXJjaGl0ZWN0dXJlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzgyOTE1MDQ3fDA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "boston-to-berkshires-car-rental": "https://images.unsplash.com/photo-1651353907119-8067027c6dd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxtYXNzYWNodXNldHRzJTIwYmVya3NoaXJlcyUyMGF1dHVtbiUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3ODIwNjg4MTN8MA&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "boston-weekly-car-rental": "https://images.unsplash.com/photo-1566222709185-7a89f1593d4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxCb3N0b24lMjBjaXR5JTIwc2t5bGluZSUyMGRvd250b3dufGVufDB8fHx8MTc4MjkyMTc4MHww&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "economy-car-rental-boston": "https://images.unsplash.com/photo-1566222709185-7a89f1593d4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxCb3N0b24lMjBjaXR5JTIwc2t5bGluZSUyMGRvd250b3dufGVufDB8fHx8MTc4MjkyMTc4MHww&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "long-term-car-rental-boston": "https://images.unsplash.com/photo-1566222709185-7a89f1593d4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxCb3N0b24lMjBjaXR5JTIwc2t5bGluZSUyMGRvd250b3dufGVufDB8fHx8MTc4MjkyMTc4MHww&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
  "boston-vacation-car-rental": "https://images.unsplash.com/photo-1566222709185-7a89f1593d4b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxCb3N0b24lMjBjaXR5JTIwc2t5bGluZSUyMGRvd250b3dufGVufDB8fHx8MTc4MjkyMTc4MHww&ixlib=rb-4.1.0&q=70&w=1200&auto=format",
};

// Per-trip vehicle-fit: sort the best-matching available cars first + tailored copy.
const FIT_PROFILES = {
  roadtrip: { pref: ["SUV", "Minivan", "Sedan", "Economy"], title: "Best cars for this trip", note: "Heading out of the city with gear? A roomy SUV or comfortable sedan makes the drive easy — here are our top picks." },
  island: { pref: ["SUV", "Minivan", "Sedan", "Economy"], title: "Best cars for the drive to the ferry", note: "You'll want room for luggage on the way to the ferry — these are our best-fit picks." },
  city: { pref: ["Economy", "Sedan", "SUV"], title: "Best cars for getting around Boston", note: "Compact and easy to park on Boston's tight streets — our most popular economy picks." },
  airport: { pref: ["Economy", "Sedan", "SUV"], title: "Popular cars, ready near Logan", note: "Land and go — here are popular picks you can pick up near the airport." },
  stadium: { pref: ["SUV", "Minivan", "Sedan", "Economy"], title: "Best cars for game day", note: "Great for the crew and tailgate gear — our roomiest options." },
  longterm: { pref: ["Sedan", "SUV", "Economy"], title: "Best cars for a longer stay", note: "For weeks or months, a comfortable sedan or SUV is the sweet spot." },
  daytrip: { pref: ["Economy", "Sedan", "SUV"], title: "Best cars for the day trip", note: "A quick, easy drive — these economy and sedan picks are perfect." },
};
const SLUG_FIT = {
  "cape-cod-car-rental": "roadtrip",
  "boston-to-cape-cod-car-rental": "roadtrip",
  "logan-airport-to-cape-cod-car-rental": "roadtrip",
  "boston-to-berkshires-car-rental": "roadtrip",
  "boston-to-plymouth-car-rental": "roadtrip",
  "boston-vacation-car-rental": "roadtrip",
  "boston-to-marthas-vineyard-car-rental": "island",
  "boston-to-nantucket-car-rental": "island",
  "boston-to-foxborough-car-rental": "stadium",
  "logan-airport-to-gillette-stadium-car-rental": "stadium",
  "economy-car-rental-boston": "city",
  "boston-airport-car-rental": "airport",
  "cheap-car-rental-logan-airport": "airport",
  "weekly-car-rental-logan-airport": "airport",
  "contactless-car-rental-boston": "airport",
  "boston-to-salem-car-rental": "daytrip",
  "logan-airport-to-salem-car-rental": "daytrip",
  "long-term-car-rental-boston": "longterm",
  "boston-weekly-car-rental": "longterm",
};

export default function LocalLanding({ slug }) {
  const cfg = LANDING_PAGES[slug];
  const [cars, setCars] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data || [])).catch(() => {});
  }, []);

  if (!cfg) return null;

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: cfg.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const rawImg = HERO_IMAGES[slug];
  const ogImage = rawImg ? (rawImg.startsWith("http") ? rawImg : `${SITE}${rawImg}`) : undefined;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: cfg.h1, item: `${SITE}/${slug}` },
    ],
  };
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Car Rental",
    name: cfg.h1,
    areaServed: { "@type": "City", name: "Boston, MA" },
    provider: { ...BELO_PROVIDER, image: ogImage || `${SITE}/belo-og.png` },
  };

  const fit = FIT_PROFILES[SLUG_FIT[slug]] || { pref: [], title: "Popular cars", note: "" };
  const rankCar = (c) => {
    const i = fit.pref.indexOf(c.category);
    return i === -1 ? 99 : i;
  };
  const featured = [...cars].sort((a, b) => rankCar(a) - rankCar(b)).slice(0, 3);

  return (
    <div data-testid={`landing-${slug}`} className="bg-white">
      <Seo
        title={cfg.metaTitle}
        description={cfg.metaDescription}
        path={`/${slug}`}
        keywords={cfg.keywords}
        image={ogImage}
        jsonLd={[faqJsonLd, breadcrumbLd, serviceLd]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-5 sm:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600/10 text-blue-700 text-sm font-medium mb-6">
            {cfg.badge}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">{cfg.h1}</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">{cfg.intro}</p>
          <div className="mt-9 max-w-2xl mx-auto">
            <AvailabilitySearch />
            <p className="mt-4 text-sm text-slate-500 flex items-center justify-center gap-1.5">
              <Phone size={14} className="text-slate-400" /> Prefer to talk? Call{" "}
              <a href="tel:+19782211451" className="text-blue-600 font-semibold hover:underline">(978) 221-1451</a>
            </p>
          </div>
        </div>
      </section>

      {/* Hero banner image */}
      {HERO_IMAGES[slug] && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-2">
          <img
            src={HERO_IMAGES[slug]}
            alt={cfg.h1}
            loading="eager"
            className="w-full aspect-[21/9] object-cover rounded-3xl shadow-card"
            data-testid="landing-hero-image"
            loading="lazy"
          />
        </div>
      )}

      {/* Highlights */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cfg.highlights.map((h, i) => (
            <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4"><h.icon size={20} /></span>
              <h3 className="font-heading font-bold text-slate-900">{h.title}</h3>
              <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fleet preview — best-fit cars for this trip */}
      {featured.length > 0 && (
        <section className="py-16 px-5 sm:px-8 max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-2 gap-4">
            <h2 className="font-heading text-3xl font-bold text-slate-900">{fit.title}</h2>
            <Link to="/fleet" className="text-blue-600 font-semibold text-sm hover:underline shrink-0">View all →</Link>
          </div>
          {fit.note && <p className="text-slate-600 mb-8 max-w-2xl leading-relaxed" data-testid="fleet-fit-note">{fit.note}</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((car, i) => <CarCard key={car.id} car={car} index={i} />)}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 px-5 sm:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-slate-900 text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-3">
            {cfg.faqs.map((f, i) => (
              <div key={i} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="font-medium text-slate-900">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related internal links (SEO cross-linking) */}
      <section className="py-16 px-5 sm:px-8 max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl font-bold text-slate-900 text-center">More Boston &amp; Logan Airport rentals</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3" data-testid="related-links">
          {RELATED_LINKS.filter(([to]) => to !== `/${slug}`).map(([to, label]) => (
            <Link key={to} to={to} className="px-4 py-2 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">
              {label}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-slate-900 to-blue-900 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Ready to book?</h2>
          <p className="mt-4 text-blue-100 max-w-xl mx-auto">Reserve a clean, well-maintained car in minutes. From $50/day.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/fleet" className="px-7 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-blue-50 transition-colors">Book your car</Link>
            <Link to="/contact" className="px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors">Contact us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
